import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Pencil, Trash2, Loader2, Store, Shield, Search, X } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const ROLE_LABELS = {
  admin_global: { label: 'Admin Global', color: 'text-red-400 bg-red-500/10' },
  manager_sucursal: { label: 'Manager', color: 'text-amber-400 bg-amber-500/10' },
  operador_pedidos: { label: 'Operador', color: 'text-blue-400 bg-blue-500/10' },
};

function RoleBadge({ role }) {
  const cfg = ROLE_LABELS[role] || { label: role, color: 'text-zinc-400 bg-zinc-800' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

function UserFormModal({ user, roles, branches, isAdmin, userBranchIds, onSave, onClose }) {
  const [form, setForm] = useState({
    nombre: user?.nombre || '',
    email: user?.email || '',
    telefono: user?.telefono || '',
    password: '',
    roles: user?.roles?.filter(Boolean) || [],
    branch_ids: user?.branch_ids?.filter(Boolean) || [],
    activo: user?.activo !== false,
  });
  const [saving, setSaving] = useState(false);

  const toggleRole = (name) => setForm(p => ({
    ...p,
    roles: p.roles.includes(name) ? p.roles.filter(r => r !== name) : [...p.roles, name],
  }));

  const toggleBranch = (id) => setForm(p => ({
    ...p,
    branch_ids: p.branch_ids.includes(id) ? p.branch_ids.filter(b => b !== id) : [...p.branch_ids, id],
  }));

  const submit = async () => {
    if (!form.nombre.trim() || !form.email.trim()) { toast.error('Nombre y email son obligatorios.'); return; }
    if (!user && !form.password) { toast.error('La contraseña es obligatoria para usuarios nuevos.'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (user) {
        if (payload.password) { payload.new_password = payload.password; }
        delete payload.password;
        await api.put(`/admin/users/${user.id}`, payload);
      } else {
        await api.post('/admin/users', payload);
      }
      toast.success(user ? 'Usuario actualizado.' : 'Usuario creado.');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar.');
    }
    setSaving(false);
  };

  const availableBranches = isAdmin ? branches : branches.filter(b => userBranchIds?.includes(b.id));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div data-testid="user-form-modal" className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">{user ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <input data-testid="user-form-name" value={form.nombre} onChange={e => setForm(p => ({...p, nombre: e.target.value}))} placeholder="Nombre *" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none" />
          <input data-testid="user-form-email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="Email *" type="email" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none" />
          <input data-testid="user-form-phone" value={form.telefono} onChange={e => setForm(p => ({...p, telefono: e.target.value}))} placeholder="Teléfono" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none" />
          <input data-testid="user-form-password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder={user ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'} type="password" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none" />
        </div>

        {/* Roles */}
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Roles</label>
          <div className="flex flex-wrap gap-2">
            {roles.map(r => (
              <button key={r.name} data-testid={`role-toggle-${r.name}`} onClick={() => toggleRole(r.name)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${form.roles.includes(r.name) ? 'bg-[#E84C1C]/20 border-[#E84C1C]/40 text-[#E84C1C]' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                {ROLE_LABELS[r.name]?.label || r.name}
              </button>
            ))}
          </div>
        </div>

        {/* Branches */}
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Sucursales asignadas</label>
          <div className="flex flex-wrap gap-2">
            {availableBranches.map(b => (
              <button key={b.id} data-testid={`branch-toggle-${b.id}`} onClick={() => toggleBranch(b.id)}
                className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 transition-colors ${form.branch_ids.includes(b.id) ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                <Store size={12} /> {b.name}
              </button>
            ))}
            {availableBranches.length === 0 && <p className="text-xs text-zinc-600">No hay sucursales disponibles.</p>}
          </div>
        </div>

        {/* Active toggle */}
        {user && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.activo} onChange={e => setForm(p => ({...p, activo: e.target.checked}))} className="rounded" />
            <span className="text-sm">Usuario activo</span>
          </label>
        )}

        <button data-testid="user-form-save" onClick={submit} disabled={saving}
          className="w-full py-3 rounded-xl bg-[#E84C1C] text-white font-semibold hover:bg-[#d43f12] disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
          {saving ? <Loader2 size={16} className="animate-spin" /> : null}
          {user ? 'Guardar cambios' : 'Crear usuario'}
        </button>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [editing, setEditing] = useState(null); // null=closed, 'new', or user object
  const isAdmin = currentUser?.roles?.includes('admin_global');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterRole) params.set('role', filterRole);
      const [uRes, rRes, bRes] = await Promise.all([
        api.get(`/admin/users?${params}`),
        api.get('/admin/users/roles'),
        api.get('/sucursales'),
      ]);
      setUsers(uRes.data.users);
      setRoles(rRes.data.roles);
      setBranches(bRes.data.sucursales || []);
    } catch { toast.error('Error cargando usuarios.'); }
    setLoading(false);
  }, [search, filterRole]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.nombre}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('Usuario eliminado.');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error.'); }
  };

  return (
    <div data-testid="users-page" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Shield size={20} className="text-[#E84C1C]" /> Gestión de Usuarios</h2>
          <p className="text-xs text-zinc-500">{users.length} usuarios</p>
        </div>
        <button data-testid="create-user-btn" onClick={() => setEditing('new')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E84C1C] text-white text-sm font-medium hover:bg-[#d43f12] transition-colors">
          <Plus size={16} /> Nuevo Usuario
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="users-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o email"
            className="w-full pl-9 pr-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-sm focus:border-[#E84C1C]/50 focus:outline-none" />
        </div>
        <select data-testid="users-filter-role" value={filterRole} onChange={e => setFilterRole(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none">
          <option value="">Todos los roles</option>
          <option value="admin_global">Admin Global</option>
          <option value="manager_sucursal">Manager</option>
          <option value="operador_pedidos">Operador</option>
        </select>
      </div>

      {/* User list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-zinc-500" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm">No se encontraron usuarios.</div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.id} data-testid={`user-row-${u.id}`}
              className="flex items-center gap-3 p-3.5 bg-zinc-900/50 border border-zinc-800/30 rounded-xl hover:border-zinc-700/50 transition-colors">
              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-zinc-400">{u.nombre?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{u.nombre}</span>
                  {!u.activo && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded-full">Inactivo</span>}
                </div>
                <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(u.roles || []).filter(Boolean).map(r => <RoleBadge key={r} role={r} />)}
                  {(u.branch_names || []).filter(Boolean).map(bn => (
                    <span key={bn} className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400 flex items-center gap-1"><Store size={8} />{bn}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)} className="p-2 rounded-lg hover:bg-zinc-800 transition-colors text-zinc-500 hover:text-zinc-300">
                  <Pencil size={14} />
                </button>
                {isAdmin && u.id !== currentUser.id && (
                  <button data-testid={`delete-user-${u.id}`} onClick={() => handleDelete(u)} className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-zinc-500 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <UserFormModal
          user={editing === 'new' ? null : editing}
          roles={roles}
          branches={branches}
          isAdmin={isAdmin}
          userBranchIds={currentUser?.branch_ids}
          onSave={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
